import { useEffect, useState, useCallback } from 'react';

interface UseZendeskWidgetOptions {
  timeout?: number;
  interval?: number;
  onReady?: () => void;
  initGlobally?: boolean;
  hideLauncher?: boolean;
}

let globalIsReady: boolean = false;
let globalIsFailed: boolean = false;

const useZendeskWidget = ({
  timeout = 30000,
  interval = 100,
  onReady,
  initGlobally = false,
  hideLauncher = false,
}: UseZendeskWidgetOptions = {}) => {
  const [isReady, setIsReady] = useState(globalIsReady);
  const [hasFailed, setHasFailed] = useState(globalIsFailed);

  const getWidget = useCallback(() => window.zE ?? null, []);

  const hideWidget = useCallback(() => {
    getWidget()?.('webWidget', 'hide');
  }, [getWidget]);

  const showWidget = useCallback(() => {
    getWidget()?.('webWidget', 'show');
  }, [getWidget]);

  const openWidget = useCallback(() => {
    getWidget()?.('webWidget', 'open');
  }, [getWidget]);

  const closeWidget = useCallback(() => {
    getWidget()?.('webWidget', 'close');
  }, [getWidget]);

  const toggleWidget = useCallback(() => {
    getWidget()?.('webWidget', 'toggle');
  }, [getWidget]);

  const prefillWidget = useCallback(
    (data: Record<string, { value: string; readOnly: boolean }>) => {
      getWidget()?.('webWidget', 'prefill', data);
    },
    [getWidget]
  );

  const updateSettings = useCallback(
    (settings: Record<string, any>) => {
      getWidget()?.('webWidget', 'updateSettings', settings);
    },
    [getWidget]
  );

  const hideHelpCenter = useCallback(() => {
    updateSettings({ helpCenter: { suppress: true } });
  }, [updateSettings]);

  const showContactForm = useCallback(() => {
    updateSettings({ contactForm: { suppress: false } });
  }, [updateSettings]);

  const openContactForm = useCallback(() => {
    hideHelpCenter();
    showContactForm();
  }, [hideHelpCenter, showContactForm]);

  useEffect(() => {
    if (globalIsReady) setIsReady(true);
    if (globalIsFailed) setHasFailed(true);
  }, []);

  useEffect(() => {
    if (!initGlobally || globalIsReady) return;

    const start = Date.now();
    const checkWidget = () => {
      const widget = getWidget();
      if (widget) {
        clearInterval(timer);
        globalIsReady = true;
        setIsReady(true);
        onReady?.();
        if (hideLauncher) hideWidget();
      } else if (Date.now() - start > timeout) {
        clearInterval(timer);
        globalIsFailed = true;
        setHasFailed(true);
      }
    };

    const timer = setInterval(checkWidget, interval);
    checkWidget();

    return () => clearInterval(timer);
  }, [getWidget, initGlobally, hideLauncher, hideWidget, timeout, interval, onReady]);

  useEffect(() => {
    const widget = getWidget();
    if (!widget || !initGlobally) return;

    const openEvent = new Event('support:hideButton');
    const closeEvent = new Event('support:showButton');

    const handleOpen = () => document.dispatchEvent(openEvent);
    const handleClose = () => {
      if (hideLauncher) hideWidget();
      document.dispatchEvent(closeEvent);
    };

    widget('webWidget:on', 'open', handleOpen);
    widget('webWidget:on', 'close', handleClose);

    return () => {
      // NOTE: Zendesk does not provide "off"; cleanup handled automatically
    };
  }, [getWidget, initGlobally, hideWidget]);

  return {
    isReady,
    hasFailed,
    widget: getWidget(),
    hideWidget,
    showWidget,
    openWidget,
    closeWidget,
    toggleWidget,
    prefillWidget,
    updateSettings,
    hideHelpCenter,
    showContactForm,
    openContactForm,
  };
};

export default useZendeskWidget;
